/**
 * Página eSocial — Fases 4.1 / 4.2 / 4.3
 * GridProfile / NeuroSynx Ltda
 *
 * Gestão dos eventos S-1200/S-1210/S-2299/S-1299. UI permite:
 *   - Painel KPIs (rascunho / assinado / transmitido / aceito / rejeitado / cancelado)
 *   - Gerar eventos a partir de competência calculada e rescisões (4.1)
 *   - Listar/filtrar por tipo, status, período
 *   - Baixar o XML / ver detalhe
 *   - Assinar XML com certificado A1 (4.2)
 *   - Transmitir lote ao gov.br + consultar protocolo (4.3, mock ou real)
 *   - Cancelar/excluir rascunhos
 *   - Gerenciar certificados A1
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  FileCode, Plus, Download, Trash2, Loader2, Building2, AlertCircle,
  CheckCircle2, XCircle, Clock, Send, FileSignature, ShieldCheck, Eye,
  Radio,
} from 'lucide-react';
import useClientPagination from '../../hooks/useClientPagination';
import GridPagination from '../GridPagination';
import EsocialKpisPanel from './EsocialKpisPanel';
import CertificadosA1Tab from './CertificadosA1Tab';
import AskMayaButton from '../AskMayaButton';
import { useLocation } from 'react-router-dom';
import { useConfirm } from '../../hooks/useConfirm';
import {
  S2200Form, S2205Form, S2206Form, S2230Form, S2298Form, S2400Form,
  S3000Form, S1000Form, S1005Form, S1010Form, S1020Form, S1070Form,
} from './forms/EsocialNonPeriodicForms';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const getErrorMsg = (e, fallback = 'Erro') => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(err => err.msg || JSON.stringify(err)).join('; ');
  if (typeof d === 'object') return d.msg || JSON.stringify(d);
  return fallback;
};

const TransmissionHistory = ({ eventoId = null }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ tipo: '', status: '', codigo_resposta: '', periodo: '' });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = eventoId ? { evento_id: eventoId } : Object.fromEntries(Object.entries(filtros).filter(([, value]) => value));
      const r = await axios.get(`${API}/api/esocial/transmissoes`, { ...auth(), params });
      setItems(r.data || []);
    } catch (e) { toast.error(getErrorMsg(e, 'Erro ao carregar histórico')); }
    finally { setLoading(false); }
  }, [eventoId, filtros]);
  useEffect(() => { load(); }, [load]);
  return <Card>
    <CardHeader className="py-3 flex flex-row items-center justify-between">
      <CardTitle className="text-base">Histórico de transmissões</CardTitle>
      <Button size="sm" variant="outline" onClick={load} disabled={loading}>Atualizar</Button>
    </CardHeader>
    <CardContent className="space-y-2">
      {!eventoId && <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <Select value={filtros.tipo || 'all'} onValueChange={(v) => setFiltros({ ...filtros, tipo: v === 'all' ? '' : v })}>
          <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger><SelectContent><SelectItem value="all">Todos os tipos</SelectItem>{Object.keys(TIPOS).map((tipo) => <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filtros.status || 'all'} onValueChange={(v) => setFiltros({ ...filtros, status: v === 'all' ? '' : v })}>
          <SelectTrigger><SelectValue placeholder="Resultado" /></SelectTrigger><SelectContent><SelectItem value="all">Todos os resultados</SelectItem>{['transmitido', 'processado_ok', 'rejeitado'].map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Código (ex.: 402)" value={filtros.codigo_resposta} onChange={(e) => setFiltros({ ...filtros, codigo_resposta: e.target.value })} />
        <Input placeholder="Período (AAAA-MM)" value={filtros.periodo} onChange={(e) => setFiltros({ ...filtros, periodo: e.target.value })} />
      </div>}
      {loading ? <div className="text-center py-4"><Loader2 className="h-4 w-4 inline animate-spin" /></div>
        : items.length === 0 ? <p className="text-sm text-slate-500">Nenhuma tentativa registrada.</p>
        : items.map((h) => <div key={h.id} className="rounded border p-3 text-xs" data-testid={`esocial-history-${h.id}`}>
          <div className="flex flex-wrap justify-between gap-2"><span className="font-medium uppercase">{h.acao.replace('_', ' ')}</span>
            <span>{h.criado_em && new Date(h.criado_em).toLocaleString('pt-BR')} · {h.origem}</span></div>
          <div className="mt-1">Status: <b>{h.status_anterior || '—'} → {h.status_posterior || '—'}</b> · Código do lote: <b>{h.codigo_resposta || h.http_status || '—'}</b></div>
          {h.descricao_resposta && <p className="mt-1 text-slate-700">{h.descricao_resposta}</p>}
          {(h.ocorrencias || []).map((o, i) => <div key={i} className="mt-2 rounded bg-red-50 p-2 text-red-800">
            <b>Erro {o.codigo || '—'}:</b> {o.descricao || 'Sem descrição'}{o.localizacao ? ` · ${o.localizacao}` : ''}
          </div>)}
        </div>)}
    </CardContent>
  </Card>;
};
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const TIPOS = {
  // Não-periódicos (trabalhador)
  'S-2200': { nome: 'Admissão',            cor: 'bg-green-50 text-green-700 border-green-300',     grupo: 'Trabalhador' },
  'S-2205': { nome: 'Alteração Cadastral', cor: 'bg-cyan-50 text-cyan-700 border-cyan-300',         grupo: 'Trabalhador' },
  'S-2206': { nome: 'Alteração Contratual',cor: 'bg-sky-50 text-sky-700 border-sky-300',            grupo: 'Trabalhador' },
  'S-2230': { nome: 'Afastamento',         cor: 'bg-orange-50 text-orange-700 border-orange-300',   grupo: 'Trabalhador' },
  'S-2298': { nome: 'Reintegração',        cor: 'bg-lime-50 text-lime-700 border-lime-300',         grupo: 'Trabalhador' },
  'S-2299': { nome: 'Desligamento',        cor: 'bg-amber-50 text-amber-700 border-amber-300',      grupo: 'Trabalhador' },
  'S-2400': { nome: 'Beneficiário RPPS',   cor: 'bg-violet-50 text-violet-700 border-violet-300',   grupo: 'Trabalhador' },
  // Periódicos (folha)
  'S-1200': { nome: 'Remuneração',         cor: 'bg-blue-50 text-blue-700 border-blue-300',         grupo: 'Folha' },
  'S-1210': { nome: 'Pagamentos',          cor: 'bg-emerald-50 text-emerald-700 border-emerald-300',grupo: 'Folha' },
  'S-1299': { nome: 'Fechamento',          cor: 'bg-purple-50 text-purple-700 border-purple-300',   grupo: 'Folha' },
  'S-1298': { nome: 'Reabertura',          cor: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-300',grupo: 'Folha' },
  // Cadastro inicial / tabelas
  'S-1000': { nome: 'Cadastro Empregador', cor: 'bg-slate-50 text-slate-700 border-slate-300',      grupo: 'Cadastro Inicial' },
  'S-1005': { nome: 'Estabelecimento',     cor: 'bg-slate-50 text-slate-700 border-slate-300',      grupo: 'Cadastro Inicial' },
  'S-1010': { nome: 'Rubrica',             cor: 'bg-slate-50 text-slate-700 border-slate-300',      grupo: 'Cadastro Inicial' },
  'S-1020': { nome: 'Lotação',             cor: 'bg-slate-50 text-slate-700 border-slate-300',      grupo: 'Cadastro Inicial' },
  'S-1070': { nome: 'Tabela de Processos', cor: 'bg-slate-50 text-slate-700 border-slate-300',      grupo: 'Cadastro Inicial' },
  // Exclusão
  'S-3000': { nome: 'Exclusão de Evento',  cor: 'bg-red-50 text-red-700 border-red-300',            grupo: 'Exclusão' },
  // SST — apenas referência (gerado em /dp-folha/sst-avancado)
  'S-2210': { nome: 'CAT (SST)',           cor: 'bg-rose-50 text-rose-700 border-rose-300',         grupo: 'SST', externo: true },
};

const GRUPOS_TIPOS = ['Trabalhador', 'Folha', 'Cadastro Inicial', 'Exclusão', 'SST'];

const STATUS_ICON = {
  rascunho:      <Clock          className="h-3 w-3 text-slate-500" />,
  assinado:      <FileSignature  className="h-3 w-3 text-blue-600" />,
  transmitido:   <Send           className="h-3 w-3 text-amber-600" />,
  processado_ok: <CheckCircle2   className="h-3 w-3 text-emerald-600" />,
  rejeitado:     <XCircle        className="h-3 w-3 text-red-600" />,
  cancelado:     <XCircle        className="h-3 w-3 text-slate-400" />,
};

// ============================================================================
// SUB-TAB: GERAR EVENTOS
// ============================================================================
const GerarEventosTab = ({ onGerado, initialTipo }) => {
  const [competencias, setCompetencias] = useState([]);
  const [rescisoes, setRescisoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    tipo: initialTipo || 'S-2200',
    competencia_id: '',
    rescisao_id: '',
    data_pagamento: '',
    ambiente: '2',
    // estado dos forms não-periódicos
    company_id: '',
    employee_id: '',
    // S-2200 defaults
    dt_admissao: '',
    cod_categ: '101',
    raca_cor: '1',
    est_civ: '1',
    grau_instr: '07',
    sexo: 'M',
    cbo_cargo: '252210',
    // S-2298 default
    tp_reint: '1',
    ind_pag_retroativo: 'N',
    // S-2400 defaults
    tp_beneficio: '0001',
    cad_ini: 'S',
    tp_plan_rp: '1',
    // S-1010 defaults
    tipo_rubrica: '1',
    // S-1020 defaults
    tp_lotacao: '01',
    fpas: '515',
    cod_tercs: '0000',
    // S-1070 defaults
    tp_proc: '1',
    ind_mat_proc: '1',
    // S-2206 default
    und_sal_fixo: '5',
  });

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/competencias`, auth()),
        axios.get(`${API}/api/calculos-dp/rescisao`, auth()),
      ]);
      setCompetencias((c.data || []).filter((cp) => ['calculada', 'fechada'].includes(cp.status)));
      setRescisoes(r.data || []);
    } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // --- helpers -------------------------------------------------------------
  const requireCompany = () => {
    if (!form.company_id) { toast.error('Selecione a empresa'); return false; }
    return true;
  };
  const requireEmployee = () => {
    if (!requireCompany()) return false;
    if (!form.employee_id) { toast.error('Selecione o funcionário'); return false; }
    return true;
  };

  // --- submit central -------------------------------------------------------
  const gerar = async () => {
    setLoading(true);
    try {
      let resp;
      const t = form.tipo;

      // Periódicos
      if (t === 'S-1200') {
        if (!form.competencia_id) return toast.error('Selecione a competência');
        resp = await axios.post(`${API}/api/esocial/s1200/gerar`,
          { competencia_id: form.competencia_id, ambiente: form.ambiente }, auth());
        toast.success(`✅ ${resp.data.gerados} eventos S-1200 gerados${resp.data.erros ? ` · ${resp.data.erros} erros` : ''}`, { duration: 5000 });
      } else if (t === 'S-1210') {
        if (!form.competencia_id || !form.data_pagamento) return toast.error('Competência e data de pagamento obrigatórias');
        resp = await axios.post(`${API}/api/esocial/s1210/gerar`,
          { competencia_id: form.competencia_id, data_pagamento: form.data_pagamento, ambiente: form.ambiente }, auth());
        toast.success(`✅ ${resp.data.gerados} eventos S-1210 gerados`);
      } else if (t === 'S-2299') {
        if (!form.rescisao_id) return toast.error('Selecione a rescisão');
        resp = await axios.post(`${API}/api/esocial/s2299/gerar`,
          { rescisao_id: form.rescisao_id, ambiente: form.ambiente }, auth());
        toast.success(`✅ S-2299 gerado · ID ${resp.data.id_esocial}`);
      } else if (t === 'S-1299') {
        if (!form.competencia_id) return toast.error('Selecione a competência');
        resp = await axios.post(`${API}/api/esocial/s1299/gerar`,
          { competencia_id: form.competencia_id, ambiente: form.ambiente }, auth());
        toast.success(`✅ S-1299 gerado${resp.data.aviso ? ` · ${resp.data.aviso}` : ''}`, { duration: 5000 });
      } else if (t === 'S-1298') {
        if (!form.competencia_id) return toast.error('Selecione a competência');
        resp = await axios.post(`${API}/api/esocial/s1298/gerar`,
          { competencia_id: form.competencia_id, ambiente: form.ambiente }, auth());
        toast.success(`✅ S-1298 gerado · reabre recibo ${resp.data.nr_rec_evt_referenciado}`, { duration: 5000 });
      }
      // SST — redireciona
      else if (t === 'S-2210') {
        toast.message('S-2210 (CAT) é gerado em: DP / Folha → SST Avançado → aba CAT', { duration: 5000 });
        window.open('/dp-folha/sst-avancado', '_blank');
        return;
      }
      // Não-periódicos — backend `esocial_cadastro` recebe company_id como QUERY param
      else if (t === 'S-2200') {
        if (!requireEmployee()) return;
        const body = {
          employee_id: form.employee_id,
          dt_admissao: form.dt_admissao,
          cod_categ: form.cod_categ, raca_cor: form.raca_cor, est_civ: form.est_civ,
          grau_instr: form.grau_instr, sexo: form.sexo,
          cbo_cargo: form.cbo_cargo, vr_sal_fx: form.vr_sal_fx || '1500.00',
          ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s2200/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-2200 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-2205') {
        if (!requireCompany()) return;
        const body = {
          cpf_trab: form.cpf_trab, dt_alteracao: form.dt_alteracao,
          nm_trab: form.nm_trab, dt_nascto: form.dt_nascto,
          sexo: form.sexo, raca_cor: form.raca_cor, est_civ: form.est_civ,
          grau_instr: form.grau_instr, nm_soc: form.nm_soc || null,
          ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s2205/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-2205 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-2206') {
        if (!requireCompany()) return;
        const body = {
          cpf_trab: form.cpf_trab, matricula: form.matricula,
          dt_alteracao: form.dt_alteracao, vr_sal_fx: form.vr_sal_fx,
          nm_cargo: form.nm_cargo || 'ANALISTA', cbo_cargo: form.cbo_cargo || '252210',
          cod_categ: form.cod_categ || '101', und_sal_fixo: form.und_sal_fixo || '5',
          ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s2206/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-2206 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-2230') {
        if (!requireCompany()) return;
        const body = {
          cpf_trab: form.cpf_trab, matricula: form.matricula,
          dt_ini_afast: form.dt_ini_afast, cod_mot_afast: form.cod_mot_afast,
          dt_fim_afast: form.dt_fim_afast || null,
          observacao: form.observacao || null, ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s2230/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-2230 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-2298') {
        if (!requireCompany()) return;
        const body = {
          cpf_trab: form.cpf_trab, matricula: form.matricula,
          tp_reint: form.tp_reint,
          dt_efet_retorno: form.dt_efet_retorno, dt_efeito_reint: form.dt_efeito_reint,
          nr_proc_jud: form.nr_proc_jud || null, nr_lei_anistia: form.nr_lei_anistia || null,
          ind_pag_retroativo: form.ind_pag_retroativo, ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s2298/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-2298 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-2400') {
        if (!requireCompany()) return;
        const body = {
          cpf_benef: form.cpf_benef, nr_beneficio: form.nr_beneficio,
          dt_ini_beneficio: form.dt_ini_beneficio,
          cad_ini: form.cad_ini, tp_beneficio: form.tp_beneficio,
          tp_plan_rp: form.tp_plan_rp, ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s2400/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-2400 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-3000') {
        if (!requireCompany()) return;
        const body = {
          tp_evento_excluir: form.tp_evento_excluir,
          nr_recibo_excluir: form.nr_recibo_excluir,
          cpf_trabalhador: form.cpf_trabalhador || null,
          ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s3000/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-3000 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-1000') {
        if (!requireCompany()) return;
        const body = {
          classe_tributaria: form.classe_tributaria, ini_validade: form.ini_validade,
          contato_nome: form.contato_nome, contato_cpf: form.contato_cpf,
          contato_fone: form.contato_fone, contato_email: form.contato_email,
          ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s1000/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-1000 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-1005') {
        if (!requireCompany()) return;
        const body = {
          cnpj_estabelecimento: form.cnpj_estabelecimento || null,
          cnae_preponderante: form.cnae_preponderante,
          aliq_rat: form.aliq_rat || null, fap: form.fap || null,
          ini_validade: form.ini_validade, ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s1005/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-1005 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-1010') {
        if (!requireCompany()) return;
        const body = {
          cod_rubrica: form.cod_rubrica, descricao_rubrica: form.descricao_rubrica,
          natureza_rubrica: form.natureza_rubrica, tipo_rubrica: form.tipo_rubrica,
          ini_validade: form.ini_validade, ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s1010/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-1010 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-1020') {
        if (!requireCompany()) return;
        const body = {
          cod_lotacao: form.cod_lotacao, tp_lotacao: form.tp_lotacao,
          cnpj_lotacao: form.cnpj_lotacao || null,
          fpas: form.fpas, cod_tercs: form.cod_tercs,
          ini_validade: form.ini_validade, ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s1020/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-1020 gerado · ${resp.data.id_esocial}`);
      } else if (t === 'S-1070') {
        if (!requireCompany()) return;
        const body = {
          tp_proc: form.tp_proc, nr_proc: form.nr_proc,
          ind_mat_proc: form.ind_mat_proc,
          ini_validade: form.ini_validade,
          fim_validade: form.fim_validade || null,
          observacao: form.observacao || null,
          ambiente: form.ambiente,
        };
        resp = await axios.post(`${API}/api/esocial/s1070/gerar?company_id=${encodeURIComponent(form.company_id)}`, body, auth());
        toast.success(`✅ S-1070 gerado · ${resp.data.id_esocial}`);
      }
      if (onGerado) onGerado();
    } catch (e) {
      toast.error(getErrorMsg(e, 'Erro ao gerar evento'));
    } finally { setLoading(false); }
  };

  // renderiza o form correto pro tipo selecionado
  const renderFormByTipo = () => {
    const props = { state: form, setState: setForm };
    switch (form.tipo) {
      case 'S-2200': return <S2200Form {...props} />;
      case 'S-2205': return <S2205Form {...props} />;
      case 'S-2206': return <S2206Form {...props} />;
      case 'S-2230': return <S2230Form {...props} />;
      case 'S-2298': return <S2298Form {...props} />;
      case 'S-2400': return <S2400Form {...props} />;
      case 'S-3000': return <S3000Form {...props} />;
      case 'S-1000': return <S1000Form {...props} />;
      case 'S-1005': return <S1005Form {...props} />;
      case 'S-1010': return <S1010Form {...props} />;
      case 'S-1020': return <S1020Form {...props} />;
      case 'S-1070': return <S1070Form {...props} />;
      default: return null;
    }
  };

  const isExterno = TIPOS[form.tipo]?.externo;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileCode className="h-5 w-5 text-indigo-600" />Gerar Evento eSocial</CardTitle>
        <CardDescription>
          Gera o XML conforme leiaute eSocial v_S_01_03_00. Após gerado (rascunho), use a aba "Eventos Gerados" para assinar com seu certificado A1 e transmitir ao gov.br.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        <div>
          <Label>Tipo de Evento *</Label>
          <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
            <SelectTrigger data-testid="esocial-gen-tipo"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-96">
              {GRUPOS_TIPOS.map(grupo => (
                <React.Fragment key={grupo}>
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50">
                    {grupo}
                  </div>
                  {Object.entries(TIPOS).filter(([, t]) => t.grupo === grupo).map(([k, t]) => (
                    <SelectItem key={k} value={k}>
                      {k} — {t.nome}{t.externo ? ' (gerado em SST)' : ''}
                    </SelectItem>
                  ))}
                </React.Fragment>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Ambiente *</Label>
          <Select value={form.ambiente} onValueChange={(v) => setForm({ ...form, ambiente: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 — Produção Restrita (homologação)</SelectItem>
              <SelectItem value="1">1 — Produção</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Periódicos: Competência */}
        {(form.tipo === 'S-1200' || form.tipo === 'S-1210' || form.tipo === 'S-1299' || form.tipo === 'S-1298') && (
          <div className="col-span-2">
            <Label>Competência *</Label>
            <Select value={form.competencia_id} onValueChange={(v) => setForm({ ...form, competencia_id: v })}>
              <SelectTrigger data-testid="esocial-gen-comp"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {competencias.length === 0 ? (
                  <SelectItem value="_" disabled>Nenhuma competência calculada</SelectItem>
                ) : competencias.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{MESES[c.mes - 1]}/{c.ano} — {c.tipo} ({c.status})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {form.tipo === 'S-1210' && (
          <div className="col-span-2">
            <Label>Data de Pagamento *</Label>
            <Input type="date" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} data-testid="esocial-gen-data" />
          </div>
        )}

        {form.tipo === 'S-2299' && (
          <div className="col-span-2">
            <Label>Rescisão *</Label>
            <Select value={form.rescisao_id} onValueChange={(v) => setForm({ ...form, rescisao_id: v })}>
              <SelectTrigger><SelectValue placeholder="Selecione a rescisão..." /></SelectTrigger>
              <SelectContent>
                {rescisoes.length === 0 ? (
                  <SelectItem value="_" disabled>Nenhuma rescisão calculada</SelectItem>
                ) : rescisoes.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.employee_nome} — {r.data_rescisao} — {r.motivo_descricao || r.motivo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Não-periódicos / Cadastros / Exclusão: forms específicos */}
        {renderFormByTipo()}

        {isExterno && (
          <div className="col-span-2 p-3 bg-rose-50 border border-rose-200 rounded text-xs flex gap-2">
            <AlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <b>S-2210 (CAT)</b> é gerado a partir do registro de Comunicação de Acidente do Trabalho. Vá em
              <b> DP / Folha → SST Avançado → aba CAT</b> e use o botão "Gerar S-2210" da CAT correspondente.
            </div>
          </div>
        )}

        <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs flex gap-2">
          <AlertCircle className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <b>Fluxo:</b> 1) Gere o XML (status "rascunho") · 2) Assine com certificado A1 ativo · 3) Transmita ao gov.br · 4) Consulte o protocolo para obter o recibo. Cada etapa atualiza o status na listagem.
          </div>
        </div>

        <div className="col-span-2">
          <Button onClick={gerar} disabled={loading || isExterno} className="w-full" data-testid="esocial-gen-btn">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando...</>
                     : isExterno
                       ? <><AlertCircle className="h-4 w-4 mr-2" />Gere o S-2210 em SST Avançado</>
                       : <><Plus className="h-4 w-4 mr-2" />Gerar Evento {form.tipo}</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// SUB-TAB: LISTAGEM (com ações 4.2/4.3)
// ============================================================================
const ListagemEventosTab = ({ refreshKey, onChange }) => {
  const { confirm, ConfirmDialog } = useConfirm();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ tipo: '', status: '', periodo: '', competencia_id: '' });
  const [detalhe, setDetalhe] = useState(null);
  const [actionLoading, setActionLoading] = useState({}); // id -> bool
  const [loteAlvo, setLoteAlvo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v));
      const r = await axios.get(`${API}/api/esocial/eventos`, { ...auth(), params });
      setEventos(r.data || []);
    } catch (e) { /* */ }
    finally { setLoading(false); }
  }, [filtros]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const { paginated, pagination, setPage } = useClientPagination(eventos, 15);

  const setBusy = (id, v) => setActionLoading((p) => ({ ...p, [id]: v }));

  const baixarXml = async (ev) => {
    try {
      const r = await axios.get(`${API}/api/esocial/eventos/${ev.id}/xml`,
        { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url; a.download = `esocial_${ev.tipo}_${ev.id_esocial}.xml`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar XML'); }
  };

  const cancelar = async (id) => {
    const ok = await confirm({
      title: 'Cancelar evento?',
      description: 'O status do evento mudará para "cancelado". Esta ação não pode ser revertida.',
      confirmLabel: 'Sim, cancelar',
    });
    if (!ok) return;
    try { await axios.post(`${API}/api/esocial/eventos/${id}/cancelar`, {}, auth()); toast.success('Cancelado'); load(); onChange && onChange(); }
    catch (e) { toast.error(getErrorMsg(e, 'Erro')); }
  };
  const excluir = async (id) => {
    const ok = await confirm({
      title: 'Excluir rascunho?',
      description: 'Esta ação é permanente.',
      confirmLabel: 'Excluir', destructive: true,
    });
    if (!ok) return;
    try { await axios.delete(`${API}/api/esocial/eventos/${id}`, auth()); toast.success('Excluído'); load(); onChange && onChange(); }
    catch (e) { toast.error(getErrorMsg(e, 'Erro')); }
  };
  const verDetalhe = async (id) => {
    try { const r = await axios.get(`${API}/api/esocial/eventos/${id}`, auth()); setDetalhe(r.data); }
    catch { toast.error('Erro'); }
  };

  const assinar = async (id) => {
    setBusy(id, true);
    try {
      await axios.post(`${API}/api/esocial/eventos/${id}/assinar`, {}, auth());
      toast.success('✅ XML assinado com certificado A1');
      load(); onChange && onChange();
    } catch (e) { toast.error(getErrorMsg(e, 'Erro ao assinar')); }
    finally { setBusy(id, false); }
  };

  const transmitir = async (id) => {
    setBusy(id, true);
    try {
      const r = await axios.post(`${API}/api/esocial/eventos/${id}/transmitir`, {}, auth());
      toast.info(`Lote recebido · Protocolo ${r.data.protocolo}. Aguardando processamento e recibo.`, { duration: 6000 });
      load(); onChange && onChange();
    } catch (e) { toast.error(getErrorMsg(e, 'Erro ao transmitir')); }
    finally { setBusy(id, false); }
  };

  const consultar = async (id) => {
    setBusy(id, true);
    try {
      const r = await axios.post(`${API}/api/esocial/eventos/${id}/consultar`, {}, auth());
      const rec = (r.data.recibos || [])[0];
      if ((r.data.ocorrencias || []).length) {
        const erro = r.data.ocorrencias[0];
        toast.error(`Rejeitado pelo eSocial · Erro ${erro.codigo || '—'}: ${erro.descricao || 'ver histórico'}`);
      } else if ((r.data.codigo_resposta || '').startsWith('2') && rec?.numero_recibo) {
        toast.success(`✓ Aceito pelo eSocial · Recibo ${rec.numero_recibo}`, { duration: 6000 });
      } else if ((r.data.codigo_resposta || '').startsWith('4') || (r.data.codigo_resposta || '').startsWith('5')) {
        toast.error(`Rejeitado pelo eSocial: ${r.data.descricao_resposta || 'ver histórico'}`);
      } else {
        toast.info('Lote ainda em processamento. A consulta automática continuará.');
      }
      load(); onChange && onChange();
    } catch (e) { toast.error(getErrorMsg(e, 'Erro ao consultar')); }
    finally { setBusy(id, false); }
  };

  const assinarLote = async () => {
    const ok = await confirm({
      title: 'Assinar todos os rascunhos?',
      description: 'Vai assinar TODOS os eventos em status "rascunho" com o certificado A1 ativo.',
      confirmLabel: 'Sim, assinar',
    });
    if (!ok) return;
    try {
      const r = await axios.post(`${API}/api/esocial/eventos/assinar-lote`, {}, auth());
      toast.success(`✅ ${r.data.assinados} assinados${r.data.erros ? ` · ${r.data.erros} erros` : ''}`, { duration: 5000 });
      load(); onChange && onChange();
    } catch (e) { toast.error(getErrorMsg(e, 'Erro')); }
  };

  const lotes = Object.values(eventos.reduce((acc, ev) => {
    if (!ev.competencia_id || !['rascunho', 'assinado'].includes(ev.status)) return acc;
    const key = `${ev.tipo}|${ev.competencia_id}`;
    acc[key] = acc[key] || { key, tipo: ev.tipo, competencia_id: ev.competencia_id, periodo: ev.periodo_apuracao, total: 0 };
    acc[key].total += 1;
    return acc;
  }, {}));

  const processarLote = async () => {
    const alvo = lotes.find((lote) => lote.key === loteAlvo);
    if (!alvo) return;
    const ok = await confirm({ title: `Transmitir ${alvo.total} evento(s) em lote?`, description: `${alvo.tipo} da competência selecionada será assinado (quando necessário) e enviado em uma única transmissão SOAP.`, confirmLabel: 'Assinar e transmitir' });
    if (!ok) return;
    setBusy('lote', true);
    try {
      await axios.post(`${API}/api/esocial/eventos/assinar-lote`, { tipo: alvo.tipo, competencia_id: alvo.competencia_id }, auth());
      const r = await axios.post(`${API}/api/esocial/eventos/transmitir-lote`, { tipo: alvo.tipo, competencia_id: alvo.competencia_id }, auth());
      toast.info(`${r.data.transmitidos} evento(s) enviados no lote ${r.data.protocolo}. Aguardando processamento.`, { duration: 6000 });
      load(); onChange && onChange();
    } catch (e) { toast.error(getErrorMsg(e, 'Erro ao transmitir lote')); }
    finally { setBusy('lote', false); }
  };

  return (
    <div className="space-y-4">
      <ConfirmDialog />
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Filtros</CardTitle>
          <div className="flex gap-2">
          <Select value={loteAlvo || 'none'} onValueChange={(v) => {
            if (v === 'none') {
              setLoteAlvo('');
              setFiltros({ ...filtros, tipo: '', competencia_id: '' });
              return;
            }
            const alvo = lotes.find((lote) => lote.key === v);
            setLoteAlvo(v);
            setFiltros({ ...filtros, tipo: alvo?.tipo || '', competencia_id: alvo?.competencia_id || '' });
          }}>
            <SelectTrigger className="w-72"><SelectValue placeholder="Selecione o lote" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Selecionar tipo e competência</SelectItem>{lotes.map((lote) => <SelectItem key={lote.key} value={lote.key}>{lote.tipo} · {(lote.periodo || '—').split('-').reverse().join('/')} · {lote.total} evento(s)</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={processarLote} disabled={!loteAlvo || actionLoading.lote} data-testid="esocial-send-batch-btn">
            {actionLoading.lote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Transmitir lote
          </Button>
          <Button size="sm" variant="outline" onClick={assinarLote} data-testid="esocial-assinar-lote-btn">
            <FileSignature className="h-4 w-4 mr-1 text-blue-600" /> Assinar todos os rascunhos
          </Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const ok = await confirm({
              title: '⚠️ Excluir TODOS os rascunhos?',
              description: 'Esta ação remove todos os eventos eSocial em status "rascunho" (respeitando filtros ativos). Operação permanente.',
              confirmLabel: 'Sim, excluir', destructive: true,
            });
            if (!ok) return;
            try {
              const params = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v));
              const r = await axios.delete(`${API}/api/esocial/eventos/limpar-rascunhos`, { ...auth(), params });
              toast.success(`🗑️ ${r.data.removidos} rascunhos removidos`);
              load(); onChange && onChange();
            } catch (e) { toast.error(getErrorMsg(e, 'Erro')); }
          }} data-testid="esocial-limpar-rascunhos-btn">
            <Trash2 className="h-4 w-4 mr-1 text-red-600" /> Limpar rascunhos
          </Button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={filtros.tipo || 'all'} onValueChange={(v) => setFiltros({ ...filtros, tipo: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.keys(TIPOS).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filtros.status || 'all'} onValueChange={(v) => setFiltros({ ...filtros, status: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {['rascunho','assinado','transmitido','processado_ok','rejeitado','cancelado'].map((s) =>
                  <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Período (YYYY-MM)</Label>
            <Input value={filtros.periodo} onChange={(e) => setFiltros({ ...filtros, periodo: e.target.value })} placeholder="2026-05" />
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-md border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Período</th>
              <th className="text-left p-3">Trabalhador</th>
              <th className="text-left p-3">ID eSocial</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Protocolo / Recibo</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={7} className="p-4 text-center text-slate-500">Nenhum evento encontrado.</td></tr>
            ) : paginated.map((e) => (
              <tr key={e.id} className="border-t hover:bg-slate-50">
                <td className="p-3"><Badge className={`${TIPOS[e.tipo]?.cor || 'bg-slate-100'} border`}>{e.tipo}</Badge></td>
                <td className="p-3">{e.periodo_apuracao}</td>
                <td className="p-3">{e.employee_nome || '—'}</td>
                <td className="p-3 font-mono text-[10px]">{e.id_esocial}</td>
                <td className="p-3"><Badge variant="outline" className="inline-flex items-center gap-1">{STATUS_ICON[e.status]} {e.status}</Badge></td>
                <td className="p-3 font-mono text-[10px]">
                  {e.protocolo && <div title="Protocolo">P: {e.protocolo}</div>}
                  {e.numero_recibo && <div className="text-emerald-700" title="Recibo">R: {e.numero_recibo}</div>}
                  {!e.protocolo && !e.numero_recibo && '—'}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => verDetalhe(e.id)} title="Detalhes / XML"
                    data-testid={`esocial-view-${e.id}`}><Eye className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => baixarXml(e)} title="Baixar XML"
                    data-testid={`esocial-dl-${e.id}`}><Download className="h-4 w-4 text-indigo-600" /></Button>
                  {e.status === 'rascunho' && (
                    <Button size="sm" variant="ghost" disabled={actionLoading[e.id]} onClick={() => assinar(e.id)} title="Assinar com A1"
                      data-testid={`esocial-sign-${e.id}`}>
                      {actionLoading[e.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4 text-blue-600" />}
                    </Button>
                  )}
                  {e.status === 'assinado' && (
                    <Button size="sm" variant="ghost" disabled={actionLoading[e.id]} onClick={() => transmitir(e.id)} title="Transmitir ao gov.br"
                      data-testid={`esocial-send-${e.id}`}>
                      {actionLoading[e.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-amber-600" />}
                    </Button>
                  )}
                  {e.status === 'transmitido' && (
                    <Button size="sm" variant="ghost" disabled={actionLoading[e.id]} onClick={() => consultar(e.id)} title="Consultar protocolo"
                      data-testid={`esocial-check-${e.id}`}>
                      {actionLoading[e.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4 text-emerald-600" />}
                    </Button>
                  )}
                  {e.status === 'rascunho' && (
                    <Button size="sm" variant="ghost" onClick={() => excluir(e.id)} title="Excluir"
                      data-testid={`esocial-del-${e.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  )}
                  {!['cancelado','processado_ok','transmitido'].includes(e.status) && (
                    <Button size="sm" variant="ghost" onClick={() => cancelar(e.id)} title="Cancelar"
                      data-testid={`esocial-cancel-${e.id}`}><XCircle className="h-4 w-4 text-amber-600" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <GridPagination pagination={pagination} onPageChange={setPage} />
      </div>

      <Dialog open={!!detalhe} onOpenChange={(o) => !o && setDetalhe(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detalhe?.tipo} — {detalhe?.id_esocial}</DialogTitle>
            <DialogDescription>
              {detalhe?.employee_nome ? `Trabalhador: ${detalhe.employee_nome} · ` : ''}
              Período: {detalhe?.periodo_apuracao} · Status: <b>{detalhe?.status}</b>
              {detalhe?.protocolo && <> · Protocolo: <code className="text-[10px]">{detalhe.protocolo}</code></>}
              {detalhe?.numero_recibo && <> · Recibo: <code className="text-[10px]">{detalhe.numero_recibo}</code></>}
            </DialogDescription>
          </DialogHeader>
          {detalhe && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">XML {detalhe.xml_assinado ? 'Assinado' : 'Não assinado'}</Label>
                <pre className="text-[10px] bg-slate-900 text-emerald-300 p-3 rounded overflow-x-auto whitespace-pre-wrap max-h-96">{detalhe.xml_assinado || detalhe.xml}</pre>
              </div>
              <TransmissionHistory eventoId={detalhe.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: TRANSMISSÃO (status global, dashboard de envios)
// ============================================================================
const TransmissaoTab = ({ refreshKey }) => {
  const [status, setStatus] = useState(null);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const carregar = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/esocial/transmissao/status`, auth()); setStatus(r.data); }
    catch { /* */ } finally { setLoading(false); }
  }, []);
  const rodarDiagnostico = useCallback(async () => {
    setLoadingDiag(true);
    try { const r = await axios.get(`${API}/api/esocial/transmissao/diagnostico`, auth()); setDiag(r.data); }
    catch (e) { toast.error(getErrorMsg(e, 'Erro no diagnóstico')); }
    finally { setLoadingDiag(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!status) return null;

  const modoBadge = status.modo === 'real'
    ? <Badge className="bg-red-600 text-white">PRODUÇÃO REAL</Badge>
    : <Badge className="bg-amber-500 text-white">MODO SIMULADO (MOCK)</Badge>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5 text-emerald-600" />Transmissão SOAP gov.br {modoBadge}</CardTitle>
        <CardDescription>
          {status.modo === 'mock'
            ? 'O sistema está simulando as respostas do gov.br. Configure ESOCIAL_TRANSMISSAO_MODO=real no backend para envios reais.'
            : 'Modo PRODUÇÃO ativo — transmissões serão enviadas ao gov.br usando o certificado A1 ativo.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase">Endpoint Homologação</div>
            <code className="text-[10px] break-all">{status.endpoint_homologacao}</code>
          </div>
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase">Endpoint Produção</div>
            <code className="text-[10px] break-all">{status.endpoint_producao}</code>
          </div>
        </div>

        <div className="border rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              <span className="font-medium">Certificado A1 ativo</span>
            </div>
            <Button size="sm" variant="outline" onClick={rodarDiagnostico} disabled={loadingDiag} data-testid="esocial-diag-btn">
              {loadingDiag ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Radio className="h-4 w-4 mr-1" />}
              Rodar Diagnóstico
            </Button>
          </div>
          {status.certificado_ativo ? (
            <div className="text-sm grid grid-cols-2 gap-2">
              <div>Titular: <b>{status.certificado_ativo.titular || '—'}</b></div>
              <div>CNPJ/CPF: <code className="font-mono">{status.certificado_ativo.titular_cpf_cnpj || '—'}</code></div>
              <div>Emissor: {status.certificado_ativo.emissor || '—'}</div>
              <div>Validade: até <b>{status.certificado_ativo.valido_ate ? new Date(status.certificado_ativo.valido_ate).toLocaleDateString('pt-BR') : '—'}</b></div>
            </div>
          ) : (
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Nenhum certificado A1 ativo. Cadastre na aba "Certificados A1".
            </p>
          )}
        </div>

        {diag && (
          <div className={`border rounded p-3 ${diag.pronto_para_transmissao_real ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`} data-testid="esocial-diag-result">
            <div className="flex items-center gap-2 mb-2">
              <Radio className={`h-4 w-4 ${diag.pronto_para_transmissao_real ? 'text-emerald-600' : 'text-amber-600'}`} />
              <span className="font-medium">Diagnóstico de Transmissão SOAP</span>
              <Badge className={diag.pronto_para_transmissao_real ? 'bg-emerald-600' : 'bg-amber-500'}>
                {diag.pronto_para_transmissao_real ? '✅ Pronto' : '⚠️ Pendente'}
              </Badge>
            </div>
            {diag.problemas.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-semibold text-red-700 mb-1">🚨 Problemas:</p>
                <ul className="text-xs list-disc list-inside text-red-700 space-y-0.5">
                  {diag.problemas.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
            {diag.avisos.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-semibold text-amber-700 mb-1">⚠️ Avisos:</p>
                <ul className="text-xs list-disc list-inside text-amber-700 space-y-0.5">
                  {diag.avisos.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
            <div className="mb-2">
              <p className="text-xs font-semibold text-slate-700 mb-1">🌐 Conectividade gov.br:</p>
              <div className="text-xs grid grid-cols-2 gap-1">
                {Object.entries(diag.endpoints_check || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1">
                    <span className="font-mono">{k}:</span>
                    {v.tls === 'OK' ? <Badge className="bg-emerald-100 text-emerald-700">OK</Badge> : <Badge className="bg-red-100 text-red-700">{v.erro || 'falha'}</Badge>}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-1">📋 Próximos passos:</p>
              <ol className="text-xs list-decimal list-inside text-slate-600 space-y-0.5">
                {diag.proximos_passos.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// PAGE
// ============================================================================
const EsocialPage = () => {
  const location = useLocation();
  const navState = location.state || {};
  const [sub, setSub] = useState(navState.tab || 'gerar');
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="esocial-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Building2 className="h-6 w-6 text-indigo-600" /> eSocial — Eventos Periódicos da Folha
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Geração, assinatura e transmissão dos XMLs S-1200 (Remuneração), S-1210 (Pagamentos), S-2299 (Desligamento) e S-1299 (Fechamento).
        </p>
        <div className="mt-2">
          <AskMayaButton prompt="Me explique como funciona o módulo eSocial do GridProfile: quais XMLs eu posso gerar, qual o fluxo de assinatura A1 e transmissão SOAP, e o que significa cada status (rascunho, assinado, transmitido, processado_ok)." testId="ask-maya-esocial" />
        </div>
      </div>

      <EsocialKpisPanel refreshKey={refreshKey} />

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="grid grid-cols-4 mb-4 w-full md:w-fit">
          <TabsTrigger value="gerar"    data-testid="esocial-tab-gerar"><Plus           className="h-4 w-4 mr-1" />Gerar Evento</TabsTrigger>
          <TabsTrigger value="lista"    data-testid="esocial-tab-lista"><FileCode       className="h-4 w-4 mr-1" />Eventos Gerados</TabsTrigger>
          <TabsTrigger value="certs"    data-testid="esocial-tab-certs"><ShieldCheck    className="h-4 w-4 mr-1" />Certificados A1</TabsTrigger>
          <TabsTrigger value="transm"   data-testid="esocial-tab-transm"><Radio         className="h-4 w-4 mr-1" />Transmissão</TabsTrigger>
        </TabsList>
        <TabsContent value="gerar"><GerarEventosTab key={navState.tipo || 'default'} initialTipo={navState.tipo} onGerado={() => { bump(); setSub('lista'); }} /></TabsContent>
        <TabsContent value="lista"><ListagemEventosTab refreshKey={refreshKey} onChange={bump} /></TabsContent>
        <TabsContent value="certs"><CertificadosA1Tab /></TabsContent>
        <TabsContent value="transm"><TransmissaoTab refreshKey={refreshKey} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default EsocialPage;
